import { useState } from 'react'
import { api } from '../api/client'
import type { ProjectLog } from '../api/types'
import { LOG_CATEGORIES, LOG_NOTE_MAX, categoryLabel, logRange } from '../lib/projectLogs'
import { formatDateTime, toLocalInput } from '../lib/time'
import { usePolled } from '../lib/usePolled'
import './project-logs.css'

/** A project's logs (Tamar, PM-31): observations, maintenance and incidents,
 *  each over a stretch of station time, on the project or one of its
 *  stations. Every member reads and adds; the author, an Editor or an Admin
 *  removes. A log never changes a reading or silences an alarm. */
export function ProjectLogs({ projectId, stations }: { projectId: number; stations: { code: string; name: string }[] }) {
  const logs = usePolled(() => api.projectLogs.list(projectId), [projectId], 30_000)
  const [note, setNote] = useState('')
  const [station, setStation] = useState('')
  const [category, setCategory] = useState<ProjectLog['category']>('note')
  const [start, setStart] = useState(() => toLocalInput(new Date(Date.now() - 3_600_000)))
  const [end, setEnd] = useState(() => toLocalInput(new Date()))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [removing, setRemoving] = useState<number | null>(null)
  const range = logRange(start, end)
  const canAdd = range !== null && note.trim() !== ''

  const add = async () => {
    if (!range || !note.trim()) return
    setBusy(true); setError('')
    try {
      await api.projectLogs.add({ project_id: projectId, station_code: station || null, ...range, category, note })
      setNote(''); logs.reload()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The log could not be saved.')
    } finally { setBusy(false) }
  }
  const remove = async (id: number) => {
    setBusy(true); setError('')
    try { await api.projectLogs.remove(id); setRemoving(null); logs.reload() }
    catch (cause) { setError(cause instanceof Error ? cause.message : 'The log could not be removed.') }
    finally { setBusy(false) }
  }
  const where = (log: ProjectLog) => log.station_code
    ? stations.find((s) => s.code === log.station_code)?.name ?? log.station_code : 'Whole project'

  return <section role="tabpanel" aria-label="Project logs" className="project-logs">
    <h3>Logs and chart notes</h3>
    <p>Record observations, maintenance and incidents. Each interval appears as a shaded area on the project's time charts. Logs do not change readings or suppress alarms.</p>
    <form onSubmit={(event) => { event.preventDefault(); void add() }}><fieldset disabled={busy}>
      <div className="pl-row">
        <label className="pl-field">Type
          <select value={category} onChange={(event) => setCategory(event.target.value as ProjectLog['category'])}>
            {LOG_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select></label>
        <label className="pl-field">Station
          <select value={station} onChange={(event) => setStation(event.target.value)}>
            <option value="">Whole project</option>
            {stations.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select></label>
        <label className="pl-field">From (station time)
          <input type="datetime-local" value={start} onChange={(event) => setStart(event.target.value)} required /></label>
        <label className="pl-field">Until (station time)
          <input type="datetime-local" value={end} onChange={(event) => setEnd(event.target.value)} required /></label>
      </div>
      <label className="pl-field">Log note
        <textarea rows={3} value={note} maxLength={LOG_NOTE_MAX} required onChange={(event) => setNote(event.target.value)} /></label>
      {!range && <p className="error" role="alert">Choose an end after the start.</p>}
      <button type="submit" className="button primary" disabled={!canAdd}>{busy ? 'Saving…' : 'Add log entry'}</button>
    </fieldset></form>
    {(error || logs.error) && <p className="error" role="alert">{error || logs.error}</p>}
    {logs.loading && !logs.data ? <p>Loading logs…</p> : !logs.data?.logs.length && !logs.error && <p>No log entries yet.</p>}
    {logs.data?.logs.map((log) => <article className="project-log-entry" key={log.id}>
      <strong>{categoryLabel(log.category)} · {where(log)}</strong>
      <p className="mono">{formatDateTime(log.starts_at)} – {formatDateTime(log.ends_at)} · station time</p>
      <p>{log.note}</p>
      <small>{log.author} · Entered {formatDateTime(log.created_at)}</small>
      {log.can_edit && (removing === log.id
        ? <span className="pl-actions">
            <button type="button" className="button" disabled={busy} onClick={() => void remove(log.id)}>Confirm remove</button>
            <button type="button" className="button" onClick={() => setRemoving(null)}>Cancel</button></span>
        : <button type="button" className="link" onClick={() => setRemoving(log.id)}>Remove entry</button>)}
    </article>)}
  </section>
}
